import React, { useCallback, useEffect, useState } from "react";
import { CompPagination, Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import { FaEdit, FaTrash } from "react-icons/fa";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { deleteData, getData } from "actions";
import { API_URL_organization } from "constants/api";
import { organizationReducers } from "redux/organizationSlice";
import { useDispatch, useSelector } from "react-redux";
import { SyncLoader } from "react-spinners";
import useDebounce from "hooks/useDebounce";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "name" },
  { title: "Website", field: "website" },
  { title: "Phone", field: "phone" },
  { title: "Actions", field: "" },
];

const Organization = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOpen, setIsOpen] = useState(0);

  const {
    getOrganizationResult,
    getOrganizationLoading,
    getOrganizationError,
    addOrganizationResult,
    deleteOrganizationResult,
  } = useSelector((state) => state.organization);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_organization,
        params,
        { dispatch, redux: organizationReducers },
        "GET_ORGANIZATION"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_organization + item.id,
      { dispatch, redux: organizationReducers },
      "DELETE_ORGANIZATION"
    );
  };

  const onEdit = (item) => {
    navigate(`/users/organizations/editorganizations`, {
      state: {
        item,
      },
    });
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addOrganizationResult) {
      fetchData();
    }
  }, [addOrganizationResult, fetchData]);

  useEffect(() => {
    if (deleteOrganizationResult) {
      fetchData();
    }
  }, [deleteOrganizationResult, fetchData]);

  return (
    <Container>
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12  w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 mb-4">
          Organization
          <div className="col-end-7 flex justify-end">
            <Link
              to={"/users/organizations/createorganizations"}
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create organization
            </Link>
          </div>
        </div>

        {/* button perpage and input search  */}
        <div className="flex flex-col md:flex-row md:justify-between align-middle px-4 mb-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by name"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
        {/* table and pagination */}
        <div className="mx-4 ">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="w-full border-collapse border">
              <thead className="text-left">
                {TABLE_HEAD.map((item, itemIdx) => (
                  <th
                    key={itemIdx}
                    className="p-2 text-sm whitespace-nowrap cursor-pointer"
                    onClick={() => {
                      item.field && handleSort(item.field);
                    }}
                  >
                    <span className="flex">
                      {item.title}
                      {item.field && renderSortIcon(item.field)}
                    </span>
                  </th>
                ))}
              </thead>
              <tbody className="text-left">
                {/* loading  */}
                {getOrganizationLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getOrganizationError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getOrganizationError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getOrganizationResult.results &&
                  getOrganizationResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getOrganizationResult.results &&
                  getOrganizationResult.results.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b bg-gray-50 dark:bg-neutral-900"
                    >
                      <td className="p-2 border text-green-700 dark:text-green-200">
                        <button
                          className="text-blue-700 dark:text-blue-300"
                          onClick={() => onEdit(item)}
                        >
                          {item.name}
                        </button>
                      </td>
                      <td className="p-2 border text-black dark:text-white">
                        {item.website}
                      </td>
                      <td className="p-2 border">{item.phone}</td>
                      <td className="p-2 border">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="pt-5 py-11">
              <CompPagination
                handlePageClick={(e) => handlePageClick(e)}
                pageCount={
                  getOrganizationResult.count > 0
                    ? getOrganizationResult.count
                    : 0
                }
                limit={limit}
                offset={offset}
                currentPage={currentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Organization;
